import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";

import { getBlueprint, listBlueprints } from "./blueprints";
import {
  createGeneration,
  generate,
  getGeneration,
  listGenerations,
} from "./generations";
import {
  createSeed,
  deleteSeed,
  getSeed,
  listSeedPresets,
  listSeeds,
  updateSeed,
} from "./seeds";

import {
  isTerminalGenerationStatus,
  type BlueprintMessage,
  type CreateGenerationRequest,
  type CreativesphereSeedType,
  type GenerateRequest,
  type GenerationMessage,
  type ListBlueprintsResponse,
  type ListGenerationsResponse,
  type ListSeedsResponse,
  type SeedMessage,
  type SeedWriteBody,
} from "@/types/creativesphere";

/** Poll cadence (ms) while a pilot generation is still PILOT_GENERATING. */
export const PILOT_POLL_INTERVAL_MS = 1_500;

/**
 * Ceiling on how long we poll a single pilot before treating it as timed out
 * (→ failed). Counted in polls, not wall-clock, since `Date.now()` is banned here.
 */
export const PILOT_POLL_MAX_MS = 120_000;
export const PILOT_POLL_MAX_ATTEMPTS = Math.ceil(
  PILOT_POLL_MAX_MS / PILOT_POLL_INTERVAL_MS,
);

// Intersection (not `extends`) since UseQueryResult is a discriminated union.
export type PollGenerationResult = UseQueryResult<GenerationMessage, Error> & {
  isPollingTimedOut: boolean;
};

export const creativesphereKeys = {
  all: ["creativesphere"] as const,
  seeds: (siteId: string, seedType: CreativesphereSeedType) =>
    [...creativesphereKeys.all, "seeds", siteId, seedType] as const,
  seedPresets: () => [...creativesphereKeys.all, "seed-presets"] as const,
  seed: (siteId: string, slug: string) =>
    [...creativesphereKeys.all, "seed", siteId, slug] as const,
  generations: (siteId: string) =>
    [...creativesphereKeys.all, "generations", siteId] as const,
  generation: (siteId: string, slug: string) =>
    [...creativesphereKeys.all, "generation", siteId, slug] as const,
  blueprints: (siteId: string) =>
    [...creativesphereKeys.all, "blueprints", siteId] as const,
  blueprint: (siteId: string, blueprintId: string) =>
    [...creativesphereKeys.all, "blueprint", siteId, blueprintId] as const,
};

export const seedsQueryOptions = (
  siteId: string,
  seedType: CreativesphereSeedType,
) =>
  ({
    queryKey: creativesphereKeys.seeds(siteId, seedType),
    queryFn: () => listSeeds(siteId, seedType),
    enabled: Boolean(siteId),
  }) satisfies Pick<
    UseQueryOptions<ListSeedsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export function useSeeds(
  siteId: string,
  seedType: CreativesphereSeedType,
  options?: Omit<
    UseQueryOptions<ListSeedsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<ListSeedsResponse, Error> {
  return useQuery<ListSeedsResponse, Error>({
    ...seedsQueryOptions(siteId, seedType),
    ...options,
  });
}

export const seedPresetsQueryOptions = () =>
  ({
    queryKey: creativesphereKeys.seedPresets(),
    queryFn: () => listSeedPresets(),
    enabled: true,
  }) satisfies Pick<
    UseQueryOptions<ListSeedsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export function useSeedPresets(
  options?: Omit<
    UseQueryOptions<ListSeedsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<ListSeedsResponse, Error> {
  return useQuery<ListSeedsResponse, Error>({
    ...seedPresetsQueryOptions(),
    ...options,
  });
}

export const seedQueryOptions = (siteId: string, slug: string) =>
  ({
    queryKey: creativesphereKeys.seed(siteId, slug),
    queryFn: () => getSeed(siteId, slug),
    enabled: Boolean(siteId && slug),
  }) satisfies Pick<
    UseQueryOptions<SeedMessage, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export function useSeed(
  siteId: string,
  slug: string,
  options?: Omit<UseQueryOptions<SeedMessage, Error>, "queryKey" | "queryFn">,
): UseQueryResult<SeedMessage, Error> {
  return useQuery<SeedMessage, Error>({
    ...seedQueryOptions(siteId, slug),
    ...options,
  });
}

/**
 * POST /v1/{site_id}/seeds — creates a seed (e.g. a Duplicate action). On
 * success, invalidates the list query for the created seed's type so the new
 * seed is picked up by the next render.
 */
export function useCreateSeed(
  siteId: string,
  options?: Omit<
    UseMutationOptions<SeedMessage, Error, SeedWriteBody>,
    "mutationFn"
  >,
): UseMutationResult<SeedMessage, Error, SeedWriteBody> {
  const queryClient = useQueryClient();
  return useMutation<SeedMessage, Error, SeedWriteBody>({
    mutationFn: (body) => createSeed(siteId, body),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: creativesphereKeys.seeds(
          siteId,
          data.seed_type as CreativesphereSeedType,
        ),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

/**
 * PATCH /v1/{site_id}/seeds/{slug} — updates a seed. On success, refreshes
 * the cached single-seed entry and invalidates the list query for its type.
 */
export function useSaveSeed(
  siteId: string,
  options?: Omit<
    UseMutationOptions<
      SeedMessage,
      Error,
      { slug: string; body: Partial<SeedWriteBody> }
    >,
    "mutationFn"
  >,
): UseMutationResult<
  SeedMessage,
  Error,
  { slug: string; body: Partial<SeedWriteBody> }
> {
  const queryClient = useQueryClient();
  return useMutation<
    SeedMessage,
    Error,
    { slug: string; body: Partial<SeedWriteBody> }
  >({
    mutationFn: ({ slug, body }) => updateSeed(siteId, slug, body),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData(
        creativesphereKeys.seed(siteId, data.slug),
        data,
      );
      void queryClient.invalidateQueries({
        queryKey: creativesphereKeys.seeds(
          siteId,
          data.seed_type as CreativesphereSeedType,
        ),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

/**
 * DELETE /v1/{site_id}/seeds/{slug} — deletes a seed. `seedType` is passed in
 * (rather than derived from the response, since DELETE has no body) so the
 * right list query can be invalidated on success.
 */
export function useDeleteSeed(
  siteId: string,
  seedType: CreativesphereSeedType,
  options?: Omit<UseMutationOptions<void, Error, string>, "mutationFn">,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (slug) => deleteSeed(siteId, slug),
    ...options,
    onSuccess: (data, slug, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: creativesphereKeys.seeds(siteId, seedType),
      });
      options?.onSuccess?.(data, slug, onMutateResult, context);
    },
  });
}

export const generationsQueryOptions = (siteId: string) =>
  ({
    queryKey: creativesphereKeys.generations(siteId),
    queryFn: () => listGenerations(siteId),
    enabled: Boolean(siteId),
  }) satisfies Pick<
    UseQueryOptions<ListGenerationsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

export function useGenerations(
  siteId: string,
  options?: Omit<
    UseQueryOptions<ListGenerationsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<ListGenerationsResponse, Error> {
  return useQuery<ListGenerationsResponse, Error>({
    ...generationsQueryOptions(siteId),
    ...options,
  });
}

export function useGeneration(
  siteId: string,
  slug: string,
  options?: Omit<
    UseQueryOptions<GenerationMessage, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<GenerationMessage, Error> {
  return useQuery<GenerationMessage, Error>({
    queryKey: creativesphereKeys.generation(siteId, slug),
    queryFn: () => getGeneration(siteId, slug),
    enabled: Boolean(siteId && slug),
    ...options,
  });
}

/**
 * Polls a pilot generation until it settles, then stops. Idle until a slug is
 * given. Bounded by {@link PILOT_POLL_MAX_ATTEMPTS}: past the ceiling it stops
 * and reports `isPollingTimedOut` so the UI can fall back to the failed state.
 */
export function usePollGeneration(
  siteId: string,
  slug: string | undefined,
  options?: Omit<
    UseQueryOptions<GenerationMessage, Error>,
    "queryKey" | "queryFn"
  >,
): PollGenerationResult {
  const queryClient = useQueryClient();
  const queryKey = creativesphereKeys.generation(siteId, slug ?? "");

  const query = useGeneration(siteId, slug ?? "", {
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data && isTerminalGenerationStatus(data.status)) return false;
      // Stop once we've polled the ceiling number of times without settling.
      if (q.state.dataUpdateCount >= PILOT_POLL_MAX_ATTEMPTS) return false;
      return PILOT_POLL_INTERVAL_MS;
    },
    ...options,
  });

  // dataUpdateCount isn't on the observer result; read it from the cache.
  const polls =
    queryClient.getQueryState<GenerationMessage>(queryKey)?.dataUpdateCount ??
    0;
  const settled = query.data
    ? isTerminalGenerationStatus(query.data.status)
    : false;
  const isPollingTimedOut = !settled && polls >= PILOT_POLL_MAX_ATTEMPTS;

  return { ...query, isPollingTimedOut };
}

/**
 * POST /v1/{site_id}/generations — kicks off a pilot generation. On success,
 * seeds the single-generation cache (so pollers start from the create response)
 * and invalidates the generations list. Poll it with {@link usePollGeneration}.
 */
export function useCreateGeneration(
  siteId: string,
  options?: Omit<
    UseMutationOptions<GenerationMessage, Error, CreateGenerationRequest>,
    "mutationFn"
  >,
): UseMutationResult<GenerationMessage, Error, CreateGenerationRequest> {
  const queryClient = useQueryClient();
  return useMutation<GenerationMessage, Error, CreateGenerationRequest>({
    mutationFn: (req) => createGeneration(siteId, req),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData(
        creativesphereKeys.generation(siteId, data.slug),
        data,
      );
      void queryClient.invalidateQueries({
        queryKey: creativesphereKeys.generations(siteId),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

/**
 * POST /v1/generate — kicks off a generation from picked seeds. On success,
 * seeds the single-generation cache (so pollers start from the create response)
 * and invalidates the generations list query for the site.
 */
export function useGenerate(
  siteId: string,
  options?: Omit<
    UseMutationOptions<GenerationMessage, Error, GenerateRequest>,
    "mutationFn"
  >,
): UseMutationResult<GenerationMessage, Error, GenerateRequest> {
  const queryClient = useQueryClient();
  return useMutation<GenerationMessage, Error, GenerateRequest>({
    mutationFn: (req) => generate(req),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData(
        creativesphereKeys.generation(siteId, data.slug),
        data,
      );
      void queryClient.invalidateQueries({
        queryKey: creativesphereKeys.generations(siteId),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export const blueprintsQueryOptions = (siteId: string) =>
  ({
    queryKey: creativesphereKeys.blueprints(siteId),
    queryFn: () => listBlueprints(siteId),
    enabled: Boolean(siteId),
  }) satisfies Pick<
    UseQueryOptions<ListBlueprintsResponse, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

/**
 * GET /v1/{siteId}/blueprints — the blueprint library for the Step 2 picker.
 * Cached under a site-scoped key so the modal grid loads instantly on reopen.
 */
export function useBlueprints(
  siteId: string,
  options?: Omit<
    UseQueryOptions<ListBlueprintsResponse, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<ListBlueprintsResponse, Error> {
  return useQuery<ListBlueprintsResponse, Error>({
    ...blueprintsQueryOptions(siteId),
    ...options,
  });
}

export const blueprintQueryOptions = (siteId: string, blueprintId: string) =>
  ({
    queryKey: creativesphereKeys.blueprint(siteId, blueprintId),
    queryFn: () => getBlueprint(siteId, blueprintId),
    enabled: Boolean(siteId && blueprintId),
  }) satisfies Pick<
    UseQueryOptions<BlueprintMessage, Error>,
    "queryKey" | "queryFn" | "enabled"
  >;

/** GET /v1/{siteId}/blueprints/{blueprintId} — a single blueprint. */
export function useBlueprint(
  siteId: string,
  blueprintId: string,
  options?: Omit<
    UseQueryOptions<BlueprintMessage, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<BlueprintMessage, Error> {
  return useQuery<BlueprintMessage, Error>({
    ...blueprintQueryOptions(siteId, blueprintId),
    ...options,
  });
}
